import { type LinkingOptions } from '@atlaskit/editor-common/types';
import { ComposableEditor } from '@atlaskit/editor-core/composable-editor';
import { EditorContext } from '@atlaskit/editor-core/editor-context';
import { SmartCardProvider } from '@atlaskit/link-provider';
import { Box, xcss } from '@atlaskit/primitives';
import React, { useCallback, useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';

import { type ADFDocument } from '@canvas/adf';
import { currentUserState } from '@canvas/canvas-state/user';
import { smartLinkClient } from '@canvas/smart-link-client';

import { ErrorBoundary } from '../error-boundary';
import { cardProvider } from '../providers/card-provider';
import { emojiProvider } from '../providers/emoji-provider';
import { getMentionProvider } from '../providers/mention-provider';
import { getEditorValue } from '../util';

import { type DatabasesEditorAPI, useDatabasesPresetWithApi } from './databases-preset';

export interface DatabasesEditorProps {
  disabled?: boolean;
  shouldFocus?: boolean;
  placeholderText?: string;
  defaultValue: ADFDocument | undefined;
  onChange: (value: ADFDocument) => void;
  onFocus?: (e: React.FocusEvent) => void;
  onBlur?: (e: React.FocusEvent) => void;
  popupsMountPoint?: HTMLElement;
  onEditorApi?: (editorApi: DatabasesEditorAPI) => void;
}

export type { DatabasesEditorAPI };

const editorContainerStyles = xcss({
  fontFamily: 'inherit',
  width: '100%',
  textAlign: 'left',
});

/**
 * The chromeless ComposableEditor applies `padding-bottom: var(--ds-space-100)`
 * to `.ProseMirror > :last-child`. This extra padding makes the edit view taller
 * than the read view. There is no prop to disable this, so we override it with
 * a scoped selector.
 */
const DATABASES_EDITOR_CSS =
  '[data-databases-editor] .ProseMirror > :last-child { padding-bottom: 0 !important; }';

const linkingOptions: LinkingOptions = {
  smartLinks: {
    provider: cardProvider,
  },
};

const DatabasesEditorInternal: React.FC<DatabasesEditorProps> = ({
  disabled,
  shouldFocus,
  placeholderText,
  defaultValue,
  onChange,
  onFocus,
  onBlur,
  popupsMountPoint,
  onEditorApi,
}) => {
  const [portalElement, setPortalElement] = useState<HTMLDivElement | null>(null);
  const mountPoint = popupsMountPoint ?? portalElement ?? undefined;

  const accountId = useSnapshot(currentUserState)?.identity?.accountId;
  const { preset, editorApi } = useDatabasesPresetWithApi({ placeholderText });

  useEffect(() => {
    onEditorApi?.(editorApi);
  }, [editorApi, onEditorApi]);

  // mountPoint from useState(portalElement) triggers re-render when ref sets it, so handleBlur stays current. onBlurCapture receives the latest handler.
  const handleBlur = useCallback(
    (e: React.FocusEvent) => {
      const relatedTarget = e.relatedTarget as Node | null;
      if (mountPoint && relatedTarget && mountPoint.contains(relatedTarget)) {
        return;
      }
      onBlur?.(e);
    },
    [mountPoint, onBlur]
  );

  const handleChange = useCallback(
    (editorView: Parameters<typeof getEditorValue>[0]) => {
      onChange?.(getEditorValue(editorView) as ADFDocument);
    },
    [onChange]
  );

  return (
    <Box
      as="div"
      xcss={editorContainerStyles}
      onFocusCapture={onFocus}
      onBlurCapture={handleBlur}
      {...{ 'data-databases-editor': true }}
    >
      <style>{DATABASES_EDITOR_CSS}</style>
      <ComposableEditor
        appearance="chromeless"
        minHeight={0}
        preset={preset}
        mentionProvider={getMentionProvider(accountId)}
        emojiProvider={emojiProvider}
        linking={linkingOptions}
        onChange={handleChange}
        disabled={disabled}
        defaultValue={defaultValue}
        popupsMountPoint={mountPoint}
        shouldFocus={shouldFocus}
      />
      {!popupsMountPoint && <Box as="div" ref={setPortalElement} />}
    </Box>
  );
};

export const DatabasesEditor: React.FC<DatabasesEditorProps> = (props) => {
  return (
    <ErrorBoundary>
      <SmartCardProvider client={smartLinkClient}>
        <EditorContext>
          <DatabasesEditorInternal {...props} />
        </EditorContext>
      </SmartCardProvider>
    </ErrorBoundary>
  );
};
